import clsx from 'clsx'
import { AlertTriangle, BatteryCharging, Gauge, Package, Route, Timer, Truck, Users } from 'lucide-react'
import type { ReactNode } from 'react'
import { fmt0, fmt1, fmtDur, num, pctTone } from './dorFormat'
import type { DorReportPayload } from './dorTypes'

function Bar({ value, className }: { value: number | null; className?: string }) {
  const w = value == null ? 0 : Math.max(0, Math.min(1, value)) * 100
  return (
    <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={clsx('h-full rounded-full transition-[width] duration-500', className)} style={{ width: `${w}%` }} />
    </div>
  )
}

function barTone(v: number | null): string {
  if (v == null) return 'bg-slate-300'
  if (v >= 0.95) return 'bg-emerald-500'
  if (v >= 0.8) return 'bg-amber-500'
  return 'bg-rose-500'
}

function Delta({ now, before, unit = '' }: { now: number | null; before: number | null; unit?: string }) {
  if (now == null || before == null || before === 0) return null
  const pct = ((now - before) / Math.abs(before)) * 100
  if (Math.abs(pct) < 0.5) return <span className="text-[10.5px] font-medium text-slate-400">= prev day</span>
  const up = pct > 0
  return (
    <span
      className={clsx('rounded px-1 py-px text-[10.5px] font-semibold', up ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700')}
      title={`Previous day: ${fmt1(before)}${unit}`}
    >
      {up ? '▲' : '▼'} {Math.abs(pct).toFixed(0)}%
    </span>
  )
}

function Card({ icon, label, value, unit, extra, sub, children }: {
  icon: ReactNode; label: string; value: string; unit?: string; extra?: ReactNode; sub?: ReactNode; children?: ReactNode
}) {
  return (
    <div className="flex min-w-0 flex-col rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-slate-600">{icon}</span>
        <span className="truncate">{label}</span>
        <span className="ml-auto">{extra}</span>
      </div>
      <div className="mt-2.5 flex items-baseline gap-1">
        <span className="text-[24px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{value}</span>
        {unit ? <span className="text-[12px] font-medium text-slate-400">{unit}</span> : null}
      </div>
      {sub ? <div className="mt-1.5 truncate text-[11.5px] text-slate-500">{sub}</div> : null}
      {children}
    </div>
  )
}

export function DorKpis({ data }: { data: DorReportPayload }) {
  const i = data.days.findIndex((x) => x.date === data.date)
  const d = data.days[i]
  const p = i > 0 ? data.days[i - 1] : undefined
  if (!d) return null
  const cur = data.months.cur
  const low = data.kms.footer[data.date]?.low_count ?? null
  const tripsPct = num(d.trips) != null && num(d.target_trips) ? d.trips! / d.target_trips! : null
  const activePct = num(d.active) != null && num(d.vehicles) ? d.active! / d.vehicles! : null
  const ld = d.loading
  const yardShare = ld && ld.total_sec ? ld.yard_sec / ld.total_sec : null
  const dash = '—'

  return (
    <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-4">
      <Card
        icon={<Route size={13} />} label="Kms run"
        value={d.kms != null ? fmt0(d.kms) : dash} unit="km"
        extra={<Delta now={d.kms} before={p?.kms ?? null} unit=" km" />}
        sub={<>Utilisation <b className={pctTone(d.utilization)}>{d.utilization != null ? `${(d.utilization * 100).toFixed(1)}%` : dash}</b> · {d.km_per_active != null ? fmt0(d.km_per_active) : dash} km / active veh</>}
      >
        <Bar value={d.utilization} className={barTone(d.utilization)} />
      </Card>
      <Card
        icon={<Truck size={13} />} label="Trips (TIMS)"
        value={d.trips != null ? fmt0(d.trips) : dash} unit={d.target_trips != null ? `/ ${fmt0(d.target_trips)}` : undefined}
        extra={<Delta now={d.trips} before={p?.trips ?? null} />}
        sub={<>MTD <b className="text-slate-700">{fmt0(cur.trips)}</b> / {cur.target_trips != null ? fmt0(cur.target_trips) : dash} · indicative {d.trips_indicative != null ? fmt1(d.trips_indicative) : dash}</>}
      >
        <Bar value={tripsPct} className={barTone(tripsPct)} />
      </Card>
      <Card
        icon={<Package size={13} />} label="Tonnage delivered"
        value={d.tonnage != null ? fmt0(d.tonnage) : dash} unit="t"
        extra={<Delta now={d.tonnage} before={p?.tonnage ?? null} unit=" t" />}
        sub={<>Target <b className={pctTone(d.tonnage_pct)}>{d.tonnage_pct != null ? `${(d.tonnage_pct * 100).toFixed(1)}%` : dash}</b> of {fmt0(d.target_tonnage)} t · MTD {fmt0(cur.tonnage)} t</>}
      >
        <Bar value={d.tonnage_pct} className={barTone(d.tonnage_pct)} />
      </Card>
      <Card
        icon={<Gauge size={13} />} label="Active vehicles"
        value={d.active != null ? fmt0(d.active) : dash} unit={d.vehicles != null ? `/ ${fmt0(d.vehicles)}` : undefined}
        extra={<Delta now={d.active} before={p?.active ?? null} />}
        sub={<>{low != null ? <><b className={low ? 'text-rose-700' : 'text-slate-700'}>{low}</b> ran ≤ {data.targets.active_min_km} km</> : 'No km data'} · MTD avg {cur.active != null ? fmt1(cur.active) : dash}</>}
      >
        <Bar value={activePct} className={barTone(activePct)} />
      </Card>
      <Card
        icon={<Timer size={13} />} label="Avg loading TAT"
        value={ld?.avg_total_sec != null ? fmtDur(ld.avg_total_sec, false) : dash} unit="h"
        sub={ld ? <>Yard {ld.avg_yard_sec != null ? fmtDur(ld.avg_yard_sec, false) : dash} · Plant {ld.avg_plant_sec != null ? fmtDur(ld.avg_plant_sec, false) : dash} · {ld.count} loadings</> : 'No Dhar visits recorded'}
      >
        <div className="mt-3 flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100" title="Yard vs plant share of loading time">
          {yardShare != null ? (
            <>
              <div className="h-full bg-amber-400" style={{ width: `${yardShare * 100}%` }} />
              <div className="h-full bg-sky-500" style={{ width: `${(1 - yardShare) * 100}%` }} />
            </>
          ) : null}
        </div>
      </Card>
      <Card
        icon={<AlertTriangle size={13} />} label="Breakdowns"
        value={fmt0(d.truck.total)} unit="trucks"
        sub={<>Accident <b className={d.truck.accident ? 'text-rose-700' : 'text-slate-700'}>{d.truck.accident}</b> · Maintenance <b className="text-slate-700">{d.truck.maintenance}</b></>}
      />
      <Card
        icon={<Users size={13} />} label="Drivers present"
        value={d.drivers_total != null ? fmt0(d.drivers_total) : dash}
        extra={<Delta now={d.drivers_total} before={p?.drivers_total ?? null} />}
        sub={Object.keys(d.drivers).length ? Object.entries(d.drivers).map(([k, v]) => `${k} ${v}`).join(' · ') : 'No dispatches'}
      />
      <Card
        icon={<BatteryCharging size={13} />} label="Energy / km"
        value={d.kwh_per_km != null ? d.kwh_per_km.toFixed(2) : dash} unit="kWh/km"
        sub={<>{d.energy_total != null ? fmt0(d.energy_total) : dash} kWh charged · Dhar {fmt0(d.energy.DHAR ?? 0)} · Jhul {fmt0(d.energy.JHUL ?? 0)} · Dhule {fmt0(d.energy.DHULE ?? 0)}</>}
      />
    </div>
  )
}
